import {
  apiCollectionCommodityBatchAdd,
  apiShoppingCartAddOrSub,
  apiShoppingCartList,
  ApiShoppingCartListResponse,
  apiShoppingCartRemove,
  apiTradeGeneralTradePreOrder,
  ApiTradeGeneralTradePreOrderRequest,
} from '@/api'
import { AsyncReturnType, RequiredDeep } from 'vtils/types'
import { clamp, keyBy } from 'vtils'
import { createModelPlus, submit, Track } from '@/utils'
import { defineProvider, useSet, UseSetResult } from '@/hooks'
import { Dialog } from 'vant'
import { globalModel } from '@/model'
import { mall } from '@/mall_jssdk'
import { navigateTo } from '@/router'
import { Ref } from '@vue/composition-api'

export interface CartGoods {
  /** 购物车记录 ID */
  id: string

  /** 销售渠道 ID */
  channelId: string

  /** 店铺 ID */
  martId: string

  /** 店铺名称 */
  martName: string

  /** 商品 ID */
  goodsId: string

  /** SKU ID */
  skuId: string

  /** 封面 */
  cover: string

  /** 名称 */
  name: string

  /** SKU */
  sku: string

  /** 标签 */
  tags: string[]

  /** 库存 */
  stock: number

  /** 价格 */
  price: number

  /** 原价 */
  cost: number

  /** 降价 */
  cutPrice: number

  /** 最小购买数量 */
  buyCountMin: number

  /** 最大购买数量 */
  buyCountMax: number

  /** 购买数量 */
  buyCount: number

  /** 是否失效 */
  invalid: boolean

  /** 失效原因 */
  invalidReason: string

  /** 原始数据 */
  raw: any
}

export interface RawCartModel {
  /** 有效的商品数据 */
  validGoodsData: CartGoods[]

  /** 无效的商品数据 */
  invalidGoodsData: CartGoods[]

  /** 有效的商品数据（通过店铺分组后的） */
  groupedValidGoodsData: Array<{
    /** 店铺ID */
    martId: string
    /** 店铺名称 */
    martName: string
    /** 商品数据 */
    goodsData: CartGoods[]
  }>

  /** 商品数据 */
  goodsData: CartGoods[]

  /** 商品 SKU ID 到详情的映射 */
  goodsMap: Record<string, CartGoods>

  /** 有效商品数量 */
  validGoodsCount: number

  /** 无效商品数量 */
  invalidGoodsCount: number

  /** 商品数量 */
  goodsCount: number

  /** 选中的商品 SKU ID 集合 */
  selectedGoodsSet: UseSetResult<string>

  /** 选中的有效商品列表 */
  selectedValidGoodsList: CartGoods[]

  /** 选中的无效商品列表 */
  selectedInvalidGoodsList: CartGoods[]

  /** 选中的商品列表 */
  selectedGoodsList: CartGoods[]

  /** 选中的商品 SKU ID 到详情的映射 */
  selectedGoodsMap: Record<string, CartGoods>

  /** 选择的有效商品数量 */
  selectedValidGoodsCount: number

  /** 选择的无效商品数量 */
  selectedInvalidGoodsCount: number

  /** 选择的商品数量 */
  selectedGoodsCount: number

  /** 选择的商品价格（仅包括有效的） */
  selectedGoodsPrice: number

  /** 是否选择了有效的 */
  isSelectValid: boolean

  /** 是否选择了失效的 */
  isSelectInvalid: boolean

  /** 是否选择了全部 */
  isSelectAll: boolean

  /** 是否选择了店铺 */
  isSelectMart: Record<string, boolean>

  /** 是否选择了商品 */
  isSelectGoods: Record<string, boolean>

  /** 选择全部商品 */
  selectAll(): void

  /** 选择店铺下的商品 */
  selectMart(group: RawCartModel['groupedValidGoodsData'][0]): void

  /** 选择有效的商品 */
  selectValid(): void

  /** 选择无效的商品 */
  selectInvalid(): void

  /** 选择单个商品 */
  selectGoods(item: CartGoods): void

  /** 当前模式 */
  mode: 'display' | 'edit'

  /** 是否展示模式 */
  isDisplayMode: boolean

  /** 是否编辑模式 */
  isEditMode: boolean

  /** 切换模式 */
  toggleMode(): void

  /** 移入收藏夹 */
  moveToFavorite(skuIds: string[]): Promise<void>

  /** 将失效的移入收藏夹 */
  moveInvalidToFavorite(skuIds: string[]): Promise<void>

  /** 将选择的移入收藏夹 */
  moveSelectedToFavorite(skuIds: string[]): Promise<void>

  /** 删除 */
  delete(skuIds: string[], silent?: boolean): Promise<void>

  /** 删除失效的 */
  deleteInvalid(): Promise<void>

  /** 删除选择的 */
  deleteSelected(): Promise<void>

  /** 结算 */
  checkout(): Promise<void>

  /** 刷新 */
  refresh(): Promise<void>

  /** 去到店铺页面 */
  navigateToMartPage(martId: string): void

  /** 去到商品页面 */
  navigateToGoodsPage(goodsId: string): void
}

export type CartModel = AsyncReturnType<typeof getCartModel>

/**
 * 规范化商品数据。
 */
function toCartGoods(
  item: RequiredDeep<ApiShoppingCartListResponse>['validList'][0],
): CartGoods {
  // 限购量: 取商品、用户的最小值
  let buyLimit = Math.min(
    item.singleDayLimitPurchases || Infinity,
    item.userDayLimitPurchases || Infinity,
  )
  buyLimit = buyLimit === Infinity ? 0 : buyLimit

  // 最小购买量: 取1和起售量的最大值
  const buyCountMin = Math.max(1, item.beginSaleQuantity)

  // 最大购买量: 取限购量和商品库存的最小值
  const buyCountMax = Math.min(
    buyLimit || item.commodityStock,
    item.commodityStock,
  )

  // 初始购买量: 取记录购买量在最小、最大间的值
  const buyCount = clamp(item.quantity, buyCountMin, buyCountMax)

  return {
    id: item.id,
    channelId: item.sellChannelId,
    martId: item.merchantId,
    martName: item.merchantName,
    goodsId: item.commodityId,
    skuId: item.skuId,
    cover: item.coverPic,
    name: item.commodityTitle,
    sku: item.skuSpecs
      .map(spec => `${spec.attributeName}:${spec.selected.value}`)
      .join(';'),
    tags: [
      item.commodityStock < item.stockAlertCount ? '库存紧张' : '',
      buyLimit ? `限购${buyLimit}件` : '',
    ].filter(Boolean),
    stock: item.commodityStock,
    price: item.currentPrice,
    cost: item.dashPrice,
    cutPrice: Math.max(mall.calculator.sub(item.price, item.currentPrice), 0),
    buyCountMin: buyCountMin,
    buyCountMax: buyCountMax,
    buyCount: buyCount,
    invalid: !!item.validReason,
    invalidReason: item.validReason,
    raw: item,
  }
}

/**
 * 获取购物车模型。
 */
export async function getCartModel() {
  const getCartData = async () => {
    const cartData: RequiredDeep<ApiShoppingCartListResponse> =
      (await apiShoppingCartList()) as any
    return {
      validGoodsData: (cartData.validList || []).map(toCartGoods),
      invalidGoodsData: (cartData.inValidList || []).map(toCartGoods),
    }
  }
  const cartData = await getCartData()
  const cartModel = createModelPlus<RawCartModel>({
    // hack: 解决「非类型化函数调用不能接受类型参数」
    ...{},
    validGoodsData: cartData.validGoodsData,
    invalidGoodsData: cartData.invalidGoodsData,
    get groupedValidGoodsData() {
      return this.validGoodsData.reduce<RawCartModel['groupedValidGoodsData']>(
        (res, item) => {
          let index = res.findIndex(x => x.martId === item.martId)
          if (index === -1) {
            index = res.length
            res.push({
              martId: item.martId,
              martName: item.martName,
              goodsData: [],
            })
          }
          res[index].goodsData.push(item)
          return res
        },
        [],
      )
    },
    get goodsData() {
      return [...this.validGoodsData, ...this.invalidGoodsData]
    },
    get goodsMap() {
      return keyBy(this.goodsData, item => item.skuId)
    },
    get validGoodsCount() {
      return this.validGoodsData.length
    },
    get invalidGoodsCount() {
      return this.invalidGoodsData.length
    },
    get goodsCount() {
      return this.goodsData.length
    },
    selectedGoodsSet: useSet<string>([]),
    get selectedValidGoodsList() {
      return this.selectedGoodsList.filter(item => !item.invalid)
    },
    get selectedInvalidGoodsList() {
      return this.selectedGoodsList.filter(item => item.invalid)
    },
    get selectedGoodsList() {
      return this.selectedGoodsSet.toArray().map(skuId => this.goodsMap[skuId])
    },
    get selectedGoodsMap() {
      return keyBy(this.selectedGoodsList, item => item.skuId)
    },
    get selectedValidGoodsCount() {
      return this.selectedValidGoodsList.length
    },
    get selectedInvalidGoodsCount() {
      return this.selectedInvalidGoodsList.length
    },
    get selectedGoodsCount() {
      return this.selectedGoodsList.length
    },
    get selectedGoodsPrice() {
      return mall.calculator.add(_ =>
        this.selectedValidGoodsList.map(item =>
          _.mul(item.price, item.buyCount),
        ),
      )
    },
    get isSelectValid() {
      return this.selectedValidGoodsCount === this.validGoodsCount
    },
    get isSelectInvalid() {
      return this.selectedInvalidGoodsCount === this.invalidGoodsCount
    },
    get isSelectAll() {
      return this.isEditMode
        ? this.selectedGoodsCount === this.goodsData.length
        : this.isSelectValid
    },
    get isSelectMart() {
      return this.groupedValidGoodsData.reduce<RawCartModel['isSelectMart']>(
        (res, group) => {
          res[group.martId] = group.goodsData.every(
            item => this.isSelectGoods[item.skuId],
          )
          return res
        },
        {},
      )
    },
    get isSelectGoods() {
      return this.goodsData.reduce<RawCartModel['isSelectGoods']>(
        (res, item) => {
          res[item.skuId] = this.selectedGoodsSet.has(item.skuId)
          return res
        },
        {},
      )
    },
    selectAll() {
      if (this.isSelectAll) {
        this.selectedGoodsSet.clear()
      } else {
        this.selectedGoodsSet.add(
          ...(this.isEditMode
            ? this.goodsData.map(item => item.skuId)
            : this.validGoodsData.map(item => item.skuId)),
        )
      }
    },
    selectMart(group) {
      if (this.isSelectMart[group.martId]) {
        this.selectedGoodsSet.delete(...group.goodsData.map(item => item.skuId))
      } else {
        this.selectedGoodsSet.add(...group.goodsData.map(item => item.skuId))
      }
    },
    selectValid() {
      if (this.isSelectValid) {
        this.selectedGoodsSet.delete(
          ...this.validGoodsData.map(item => item.skuId),
        )
      } else {
        this.selectedGoodsSet.add(
          ...this.validGoodsData.map(item => item.skuId),
        )
      }
    },
    selectInvalid() {
      if (this.isSelectInvalid) {
        this.selectedGoodsSet.delete(
          ...this.invalidGoodsData.map(item => item.skuId),
        )
      } else {
        this.selectedGoodsSet.add(
          ...this.invalidGoodsData.map(item => item.skuId),
        )
      }
    },
    selectGoods(item) {
      this.selectedGoodsSet.toggle(item.skuId)
    },
    mode: 'display',
    get isDisplayMode() {
      return this.mode === 'display'
    },
    get isEditMode() {
      return this.mode === 'edit'
    },
    toggleMode() {
      this.mode = this.isDisplayMode ? 'edit' : 'display'
    },

    async moveToFavorite(skuIds) {
      await submit(async $ => {
        if (!skuIds.length) {
          return $.fail('请选择商品')
        }
        $.start()
        await apiCollectionCommodityBatchAdd({
          commodityList: skuIds.map(skuId => ({
            commodityId: this.goodsMap[skuId].goodsId,
            skuId: this.goodsMap[skuId].skuId,
            fromShoppingCartFlag: 1,
            sellChannelId: this.goodsMap[skuId].channelId,
          })),
        })
        await this.delete(skuIds, true)
        await $.success('操作成功')
      })
    },

    async moveInvalidToFavorite() {
      return this.moveToFavorite(this.invalidGoodsData.map(item => item.skuId))
    },

    async moveSelectedToFavorite() {
      return this.moveToFavorite(this.selectedGoodsList.map(item => item.skuId))
    },

    async delete(skuIds, silent = false) {
      const run = async () => {
        await apiShoppingCartRemove({
          idList: skuIds.map(skuId => this.goodsMap[skuId].id),
        })
        await this.refresh()
      }
      if (silent) {
        await run()
      } else {
        await submit(async $ => {
          if (!skuIds.length) {
            return $.fail('请选择商品')
          }
          await Dialog.confirm({
            message: `确认将这${skuIds.length}个宝贝删除吗？`,
          })
          $.start()
          await run()
          await $.success('操作成功')
        })
      }
    },

    async deleteInvalid() {
      return this.delete(this.invalidGoodsData.map(item => item.skuId))
    },

    async deleteSelected() {
      return this.delete(this.selectedGoodsList.map(item => item.skuId))
    },

    async checkout() {
      await submit(async $ => {
        if (!this.selectedGoodsCount) {
          return $.fail('请选择要结算的商品')
        }
        $.start()
        const preOrderRequestData: ApiTradeGeneralTradePreOrderRequest = {
          preOrderType: '1',
          skuInfo: this.selectedGoodsList.map(item => ({
            commodityId: item.goodsId,
            count: item.buyCount,
            skuId: item.skuId,
            sellChannelId: item.channelId,
          })),
        }
        const data = await apiTradeGeneralTradePreOrder(preOrderRequestData)
        Track.pushEvent({
          type: 'click',
          name: '提交订单',
          id: 'TiJiaoDinDan',
          extra: {
            order_id: data.preOrderId,
            order_price: data.orderAmount,
            commodity_id: this.selectedGoodsList
              .map(item => item.goodsId)
              .join(','),
            commodity_name: this.selectedGoodsList
              .map(item => item.name)
              .join(','),
            original_price: data.orderAmount,
            present_price: data.orderAmount,
            discount_price: 0,
            submit_type: '去结算',
          },
        })
        navigateTo(globalModel.state.isTV ? 'TvOrderPlace' : 'OrderPlace', {
          type: 'normal',
          data: JSON.stringify(data),
          preOrderRequestData: JSON.stringify(preOrderRequestData),
        })
      })
    },

    async refresh() {
      const cartData = await getCartData()
      this.validGoodsData = cartData.validGoodsData
      this.invalidGoodsData = cartData.invalidGoodsData
      this.selectedGoodsSet.intersect(...this.goodsData.map(item => item.skuId))
    },

    navigateToMartPage(martId) {
      navigateTo('MartIndex', {
        id: martId,
      })
    },

    navigateToGoodsPage(goodsId) {
      navigateTo('GoodsIndex', {
        id: goodsId,
      })
    },
  })

  // 同步购买数量到后端
  let offBuyCountWatch: Array<() => void> = []
  cartModel.$watch(
    () => cartModel.validGoodsData,
    () => {
      offBuyCountWatch.forEach(off => off())
      offBuyCountWatch = []
      cartModel.validGoodsData.forEach(item => {
        offBuyCountWatch.push(
          cartModel.$watch(
            () => item.buyCount,
            () => {
              apiShoppingCartAddOrSub({
                id: item.id,
                // 需限制最大为库存，不然输入很大的数组后端会报错
                quantity: Math.min(item.buyCount, item.stock),
              })
            },
          ),
        )
      })
    },
    {
      immediate: true,
    },
  )

  return cartModel
}

/**
 * 购物车模型提供者。
 */
export const cartModelProvider = defineProvider<Ref<CartModel>>()
